/**
 * # Bitfield Collision System
 *
 * This module provides utilities for managing physics collision groups using bitfield operations.
 * Each collision group is represented as a power of 2 (single bit), allowing efficient collision
 * filtering using bitwise operations.
 *
 * ## How It Works:
 * - Each group gets a unique bit position: Group1 = 1, Group2 = 2, Group3 = 4, Group4 = 8...
 * - Collision masks are created by combining groups with bitwise OR: `Group1 | Group3 = 5`
 * - p2.js uses these masks to determine if two bodies should collide
 *
 * ## 32-Group Limitation:
 * JavaScript bitwise operations work on 32-bit integers, limiting us to 32 collision groups.
 * In practice, this is rarely a problem as most games need far fewer distinct collision types.
 *
 * ## Usage Examples:
 * ```typescript
 * // Define collision groups for your game
 * const CollisionGroups = makeCollisionGroups([
 *   "Player", "Enemy", "Projectile", "Wall", "Pickup"
 * ]);
 *
 * // Set up collision filtering
 * playerShape.collisionGroup = CollisionGroups.Player;
 * playerShape.collisionMask = CollisionGroups.Enemy | CollisionGroups.Wall | CollisionGroups.Pickup;
 *
 * wallShape.collisionGroup = CollisionGroups.Wall;
 * wallShape.collisionMask = CollisionGroups.All; // Collides with everything
 *
 * projectileShape.collisionGroup = CollisionGroups.Projectile;
 * projectileShape.collisionMask = CollisionGroups.Enemy | CollisionGroups.Wall; // Not players or pickups
 * ```
 *
 * ## Common Patterns:
 * - **Projectile Filtering**: Projectiles collide with enemies/walls but not the player who fired them
 * - **Layer Separation**: UI elements, background, foreground physics on different groups
 * - **Trigger Zones**: Sensors that detect specific entity types without blocking movement
 * - **Team-Based**: Different teams or factions that don't collide with each other
 */

let _collisionGroupIndex = 0;

/**
 * Creates a unique collision group using the next available bit position.
 * Each call returns a power of 2: 1, 2, 4, 8, 16, 32...
 *
 * @returns A unique collision group bit mask
 * @throws Error if more than 32 groups are created (JavaScript bitwise limitation)
 */
export function makeCollisionGroup() {
  if (_collisionGroupIndex >= 32) {
    throw new Error(
      "Cannot create more than 32 collision groups due to JavaScript bitwise limitations",
    );
  }
  return 1 << _collisionGroupIndex++;
}

/**
 * Creates a collision group registry from an array of group names.
 * Automatically generates bit mask values and includes 'All' and 'None' groups.
 *
 * The returned object provides type-safe access to collision groups and includes:
 * - **None**: 0 (collides with nothing)
 * - **All**: Combined mask of all defined groups (collides with everything)
 * - **[GroupName]**: Individual group masks for each provided name
 *
 * @param groupNames Array of collision group names to create
 * @returns Type-safe object mapping group names to bit masks
 *
 * @example
 * ```typescript
 * const Groups = makeCollisionGroups(["Player", "Enemy", "Wall"]);
 * // Groups.Player = 1, Groups.Enemy = 2, Groups.Wall = 4
 * // Groups.All = 7 (1 | 2 | 4), Groups.None = 0
 *
 * // Use with p2.js shapes
 * shape.collisionGroup = Groups.Player;
 * shape.collisionMask = Groups.Enemy | Groups.Wall;
 * ```
 */
export function makeCollisionGroups<const T extends readonly string[]>(
  groupNames: T,
): Readonly<Record<T[number] | "All" | "None", number>> {
  const groups: Record<string, number> = {
    None: 0,
  };

  for (const name of groupNames) {
    groups[name] = makeCollisionGroup();
  }

  groups.All = Object.values(groups)
    .filter((value) => value !== 0)
    .reduce((acc, group) => acc | group, 0);

  return groups as Record<T[number] | "All" | "None", number>;
}
